import { type ReactNode } from 'react';

import { type RequestContext } from '~/vendor/cx-web/domain-types';

import { type BettingInsightsContent, type EsIndex } from '../types';

// Type for the context that will be passed as first parameter to render functions
export interface BettingInsightsTableFooterContext {
  environment: RequestContext['environment'];
  featureFlags: RequestContext['featureFlags'];
  repubConfig: RequestContext['repubConfig'];
  user: RequestContext['user'];
}

export interface BettingInsightsTableFooterConfig<TIndex extends EsIndex> {
  render: (
    context: BettingInsightsTableFooterContext,
    data: {
      content: BettingInsightsContent<TIndex>;
      resourcesUrl: string;
      resultIndex: number;
    },
  ) => ReactNode;
}

// Type alias for the return type of createBettingInsightsTableFooter
export type BettingInsightsTableFooter<TIndex extends EsIndex> = Readonly<{
  render: (data: { content: BettingInsightsContent<TIndex>; resourcesUrl: string; resultIndex: number }) => ReactNode;
}>;

/**
 * Factory function to create BettingInsightsTableFooter configuration (functional approach)
 * @param requestContext - The request context
 * @param config - The table footer configuration
 * @returns Immutable table footer configuration object
 */
export function createBettingInsightsTableFooter<TIndex extends EsIndex>(
  requestContext: RequestContext,
  config: BettingInsightsTableFooterConfig<TIndex>,
): BettingInsightsTableFooter<TIndex> {
  // Create context object
  const context: BettingInsightsTableFooterContext = {
    environment: requestContext.environment,
    featureFlags: requestContext.featureFlags,
    repubConfig: requestContext.repubConfig,
    user: requestContext.user,
  };

  // Create the result object with wrapper function that injects context
  const result = {
    // Wrapper function that injects context as first parameter
    render: (data: { content: BettingInsightsContent<TIndex>; resourcesUrl: string; resultIndex: number }) =>
      config.render(context, data),
  };

  return Object.freeze(result);
}
